import { Plus, Trash2 } from "lucide-react";
import { useMemo } from "react";

const PRESET_FIELDS = [
  { value: "status", label: "Status" },
  { value: "state", label: "State" },
  { value: "country", label: "Country" },
  { value: "region", label: "Region" },
  { value: "tags", label: "Tags" },
  { value: "type", label: "Type" },
  { value: "artist_id", label: "Artist ID" },
  { value: "__custom__", label: "Custom…" },
];

export type FilterRow = {
  id: string;
  field: string;
  customField?: string;
  value: string;
};

interface FilterBuilderProps {
  filters: FilterRow[];
  onChange: (filters: FilterRow[]) => void;
}

export function FilterBuilder({ filters, onChange }: FilterBuilderProps) {
  const rows = useMemo(() => filters, [filters]);

  const updateRow = (id: string, patch: Partial<FilterRow>) => {
    onChange(rows.map((row) => (row.id === id ? { ...row, ...patch } : row)));
  };

  const addRow = () => {
    const newId =
      typeof crypto !== "undefined" && typeof crypto.randomUUID === "function"
        ? crypto.randomUUID()
        : `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    onChange([
      ...rows,
      { id: newId, field: PRESET_FIELDS[0]?.value ?? "status", value: "" },
    ]);
  };

  const removeRow = (id: string) => {
    onChange(rows.filter((row) => row.id !== id));
  };

  if (!rows.length) {
    return (
      <div className="rounded-xl border border-border bg-background/60 p-4 text-sm text-muted-foreground">
        <p>No filters yet.</p>
        <button
          type="button"
          onClick={addRow}
          className="mt-3 inline-flex items-center gap-2 rounded-lg border border-border px-3 py-1.5 text-xs font-semibold uppercase tracking-wide text-foreground/70 hover:text-foreground"
        >
          <Plus size={14} />
          Add filter
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-3 rounded-xl border border-border bg-background/60 p-4 text-sm text-foreground/70">
      {rows.map((row) => {
        const isCustom = row.field === "__custom__";
        return (
          <div
            key={row.id}
            className="flex flex-wrap items-center gap-3 rounded-lg border border-border bg-background/80 p-3"
          >
            <label className="text-xs uppercase tracking-wide text-muted-foreground/80">
              Field
              <select
                className="mt-2 w-40 rounded-md border border-border bg-background px-2 py-1 text-sm text-foreground"
                value={row.field}
                onChange={(event) => updateRow(row.id, { field: event.target.value })}
              >
                {PRESET_FIELDS.map((preset) => (
                  <option key={preset.value} value={preset.value}>
                    {preset.label}
                  </option>
                ))}
              </select>
            </label>
            {isCustom && (
              <label className="text-xs uppercase tracking-wide text-muted-foreground/80">
                Custom key
                <input
                  type="text"
                  className="mt-2 w-40 rounded-md border border-border bg-background px-2 py-1 text-sm text-foreground"
                  value={row.customField ?? ""}
                  placeholder="metadata key"
                  onChange={(event) => updateRow(row.id, { customField: event.target.value })}
                />
              </label>
            )}
            <label className="flex-1 text-xs uppercase tracking-wide text-muted-foreground/80">
              Value
              <input
                type="text"
                className="mt-2 w-full rounded-md border border-border bg-background px-2 py-1 text-sm text-foreground"
                value={row.value}
                placeholder="Value matches"
                onChange={(event) => updateRow(row.id, { value: event.target.value })}
              />
            </label>
            <button
              type="button"
              onClick={() => removeRow(row.id)}
              className="inline-flex items-center gap-1 rounded-md border border-rose-500/40 px-3 py-1 text-xs font-semibold uppercase tracking-wide text-rose-200 hover:bg-rose-500/10"
            >
              <Trash2 size={14} />
              Remove
            </button>
          </div>
        );
      })}
      <button
        type="button"
        onClick={addRow}
        className="inline-flex items-center gap-2 rounded-lg border border-border px-3 py-1.5 text-xs font-semibold uppercase tracking-wide text-foreground/70 hover:text-foreground"
      >
        <Plus size={14} />
        Add filter
      </button>
    </div>
  );
}
